import React, { useEffect, useState } from "react";
import { Spin, Button, Space, Typography, Row, Col, Card } from "antd";
import {
  DownloadOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import axios from "../../services/axiosInstance";
import { useParams } from "react-router-dom";
import {
  FETCH_SIGNED_DOCUMENT_FROM_EMAIL,
  TOGGLE_REMINDER_EMAIL_AS_RENEWED,
} from "../../services/apiEndPoints";
import { toastSuccess, toastError } from "../../utils/toast";

const { Title, Text } = Typography;

export const isPdf = (url: string) => url.toLowerCase().includes(".pdf");

const DocumentSingleComponent: React.FC = () => {
  const params = useParams();
  const documentId = params.id;

  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const fetchSignedUrl = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        FETCH_SIGNED_DOCUMENT_FROM_EMAIL(documentId || "")
      );
      if (response?.data?.results) {
        setSignedUrl(response.data.results);
      } else {
        throw new Error("Invalid response");
      }
    } catch {
      toastError("Failed to load document");
    } finally {
      setLoading(false);
    }
  };

  const handleRenewToggle = async (id: string) => {
    try {
      setRenewing(true);
      await axios.put(TOGGLE_REMINDER_EMAIL_AS_RENEWED(id), {});
      toastSuccess("Document marked as renewed");
    } catch {
      toastError("Failed to update renewal status");
    } finally {
      setRenewing(false);
    }
  };

  useEffect(() => {
    if (documentId) {
      fetchSignedUrl();
    } else {
      toastError("No document ID provided");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  return (
    <div>
      {/* Page header */}
      <Row justify="space-between" align="middle" className="page-header-row">
        <Col>
          <Title level={4} className="page-title">
            <FileTextOutlined style={{ marginRight: 8 }} />
            Document Preview
          </Title>
          <Text className="page-subtitle">
            View, download, or mark as renewed
          </Text>
        </Col>
        {!loading && signedUrl && (
          <Col>
            <Space>
              <Button
                icon={<CheckCircleOutlined />}
                onClick={() => documentId && handleRenewToggle(documentId)}
                loading={renewing}
              >
                Mark as Renewed
              </Button>
              <a
                href={signedUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button type="primary" icon={<DownloadOutlined />}>
                  Download
                </Button>
              </a>
            </Space>
          </Col>
        )}
      </Row>

      {loading && (
        <div style={{ textAlign: "center", padding: 80 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading document...</Text>
          </div>
        </div>
      )}

      {!loading && signedUrl && (
        <Card
          styles={{ body: { padding: 0 } }}
          style={{
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid #e2e8f0",
          }}
        >
          {isPdf(signedUrl) ? (
            <iframe
              src={signedUrl}
              width="100%"
              style={{ height: "78vh", border: "none", display: "block" }}
              title="PDF Preview"
            />
          ) : (
            <div
              style={{
                padding: 24,
                display: "flex",
                justifyContent: "center",
                background: "#f8fafc",
              }}
            >
              <img
                src={signedUrl}
                alt="Document Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "75vh",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
              />
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default DocumentSingleComponent;
